import { useState } from 'react';
import { MdCreateNewFolder } from 'react-icons/md';
import {
  Button,
  Folder as MoveDocumentFolder,
  FolderSelect,
  Modal,
  ModalFooter,
  useToastNotifications,
} from '@nbirl/nbi-common-ui-libs';

import { useErrorToast } from '../../../../../../shared/hooks';
import { NewFolder } from '../../../../folders/components';
import { useFolderTree } from '../../../../folders/context';
import { SecureDocument } from '../../../models';
import { useMoveDocumentMutation } from '../../../queries';

type MoveDocumentModalProps = {
  document: SecureDocument;
  isOpen?: boolean;
  onDismiss: () => void;
  onSuccess: () => void;
};

export const MoveDocumentModal = ({
  document,
  isOpen,
  onDismiss,
  onSuccess,
}: MoveDocumentModalProps) => {
  const { data } = useFolderTree();
  const foldersTree = data ?? [];
  const [selectedFolder, setSelectedFolder] =
    useState<MoveDocumentFolder | null>(null);
  const moveDocumentMutation = useMoveDocumentMutation();
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const { addToast } = useToastNotifications();
  const { showErrorToast } = useErrorToast();
  const { id: documentId, name: documentName } = document;

  const handleDismiss = () => {
    setSelectedFolder(null);
    onDismiss();
  };

  const handleMoveDocument = () => {
    if (!selectedFolder) return;
    handleDismiss();
    moveDocumentMutation.mutate(
      { documentId, folderId: selectedFolder?.id },
      {
        onSuccess: () => {
          addToast({
            variant: 'success',
            text: `Document "${documentName}" successfully moved.`,
          });
          onSuccess();
        },
        onError: () => {
          showErrorToast(
            'Something went wrong. The document could not be moved.'
          );
        },
      }
    );
  };

  return (
    <Modal
      heading={`Move "${documentName}"`}
      isOpen={isOpen}
      onDismiss={handleDismiss}
    >
      <div className="-mt-8 mb-8 text-gray-400 text-sm">
        This document will only be visbile to those that have access to the
        folder
      </div>

      {showCreateFolder ? (
        <NewFolder
          parentId={selectedFolder?.id}
          onSuccess={onSuccess}
          onClose={() => setShowCreateFolder(false)}
        />
      ) : (
        <FolderSelect
          folders={foldersTree}
          value={selectedFolder}
          onChange={setSelectedFolder}
        />
      )}

      <ModalFooter>
        <div className="flex-1">
          <Button
            variant="ghost"
            leftIcon={<MdCreateNewFolder />}
            onClick={() => setShowCreateFolder(true)}
            disabled={showCreateFolder}
          >
            Create folder
          </Button>
        </div>
        <Button
          disabled={!selectedFolder || showCreateFolder}
          onClick={handleMoveDocument}
        >
          Move
        </Button>
      </ModalFooter>
    </Modal>
  );
};
